import { add, listen, qs, qsa, remove } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { createFocusTrap } from "focus-trap";
import isMobile from "is-mobile";

import DrawerSearch from "@/lib/drawer-search";
import { prefersReducedMotion } from "@/lib/a11y";
import { FadeUpReveal } from "@/lib/animation";

const sel = {
  overlay: "[data-overlay]",
  listItem: "[data-list-item]",
  item: "[data-item]",
  allLinks: "[data-all-links]",
  main: "[data-main]",
  primary: "[data-primary-container]",
  animationItems:
    ".drawer-menu__primary-links > .drawer-menu__item, .drawer-menu__primary-links > .drawer-menu__form",

  // Cross border
  form: ".drawer-menu__form",
  localeInput: "[data-locale-input]",
  currencyInput: "[data-currency-input]",
};

const classes = {
  active: "active",
  visible: "visible",
  childVisible: "child-visible",
};

const { enableRevealAnimations } = document.body.dataset;

// Extra space we add to the height of the inner container
const formatHeight = h => h + 8 + "px";
const formatHeightChild = h => h + 168 + "px";
const formatHeightChildMobile = h => h + 108 + "px";

const menu = node => {
  const focusTrap = createFocusTrap(node);

  // The individual link list the merchant selected
  let linksDepth = 0;

  // This is the element that holds the one we move left and right (primary)
  // We also need to assign its height initially so we get smooth transitions
  const main = qs(sel.main, node);

  // Element that holds all the primary links and moves left and right
  const primary = qs(sel.primary, node);

  // Cross border
  const form = qs(sel.form, node);
  const localeInput = qs(sel.localeInput, node);
  const currencyInput = qs(sel.currencyInput, node);

  const search = DrawerSearch(node);

  // Nodes
  const overlay = qs(sel.overlay, node);
  const parents = qsa('[data-item="parent"]', node);
  const parentBack = qsa('[data-item="back"]', node);
  const languages = qsa('[data-item="locale"]', node);
  const currencies = qsa('[data-item="currency"]', node);
  const closeButtons = qsa("[data-drawer-close]", node);
  const animationItems = qsa(sel.animationItems, node);

  // Init animation
  const reveals = [];
  initRevealAnimation();

  const events = [
    // Click on overlay or close button
    listen(overlay, "click", close),
    listen(closeButtons, "click", close),

    // Esq pressed
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) close();
    }),

    // Element that will navigate to child navigation list
    listen(parents, "click", clickParent),

    // Element that will navigate back up the tree
    listen(parentBack, "click", clickBack),

    // Individual language
    listen(languages, "click", e => handleCrossBorder(e, localeInput)),

    // // Individual currency
    listen(currencies, "click", e => handleCrossBorder(e, currencyInput)),
  ];

  function initRevealAnimation() {
    if (!prefersReducedMotion() && enableRevealAnimations === "true") {
      animationItems.forEach(item => {
        reveals.push(new FadeUpReveal(item, { yStart: "40px" }));
      });
    }
  }

  function open() {
    add(node, classes.active);

    if (!prefersReducedMotion() && enableRevealAnimations === "true") {
      reveals.forEach((reveal, i) => {
        reveal.play(window.theme.animation.delayExtraShort * i);
      });
    }

    setTimeout(() => {
      add(node, classes.visible);
      focusTrap.activate();

      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });

      if (linksDepth === 0) {
        main.style.height = formatHeight(primary.offsetHeight);
      }
    }, 50);
  }

  function close() {
    focusTrap.deactivate();
    remove(node, classes.visible);
    setTimeout(() => {
      remove(node, classes.active);
      enableBodyScroll(node);
      reveals.forEach(reveal => reveal.init());
    }, 660);
  }

  function clickParent(e) {
    e.preventDefault();

    const link = e.currentTarget;
    const childMenu = link.nextElementSibling;
    const firstFocusable = qs(".drawer-menu__link", childMenu);

    add(childMenu, classes.visible);
    add(link.parentNode, classes.childVisible);

    if (childMenu.hasAttribute("data-search-menu")) {
      add(node, "search-active");
    }

    main.style.height = isMobile()
      ? formatHeightChildMobile(childMenu.offsetHeight)
      : formatHeightChild(childMenu.offsetHeight);

    navigate((linksDepth += 1));

    link.setAttribute("aria-expanded", true);
    childMenu.setAttribute("aria-hidden", false);

    setTimeout(() => {
      firstFocusable.focus();
    }, 50);
  }

  function navigate(depth) {
    linksDepth = depth;
    primary.setAttribute("data-depth", depth);
  }

  function clickBack(e) {
    e.preventDefault();

    const menuBefore = e.currentTarget.closest(sel.listItem).closest("ul");
    const firstFocusable = qs(".drawer-menu__link", menuBefore);
    const menu = e.currentTarget.closest("ul");
    const parentLink = qs(".drawer-menu__link", menu.parentNode);

    remove(menu, classes.visible);
    remove(parentLink.parentNode, classes.childVisible);
    navigate((linksDepth -= 1));

    parentLink.setAttribute("aria-expanded", false);
    menu.setAttribute("aria-hidden", true);

    if (linksDepth === 0) {
      main.style.height = formatHeight(menuBefore.offsetHeight);
    } else {
      main.style.height = isMobile()
        ? formatHeightChildMobile(menuBefore.offsetHeight)
        : formatHeightChild(menuBefore.offsetHeight);
    }

    if (menu.hasAttribute("data-search-menu")) {
      search.clear();
      remove(node, "search-active");
    }

    setTimeout(() => {
      firstFocusable.focus();
    }, 50);
  }

  function handleCrossBorder(e, input) {
    const { value } = e.currentTarget.dataset;
    input.value = value;
    close();
    form.submit();
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    enableBodyScroll(node);
    search.destroy();
  }

  return { close, destroy, open };
};

export default menu;
